import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { api } from '../../api';
import { showToast } from '../../components/ui/toast';
import { icon } from '../../components/ui/icons';
import { t } from '../../i18n';
import { isImageKey } from '../../media-meta';
import '../../components/ui/album-view';

interface Album {
  id: string; name: string; description: string | null; item_count?: number;
  cover_url?: string | null; cover_type?: 'photo' | 'video' | null;
}

const mediaUrl = (key: string | null | undefined) => (key ? `/api/v1/media/${key}` : '');

/**
 * <cadence-portal-event-gallery> — the student-facing photos/videos for one event. Browse
 * the event's albums (approved media only) and submit your own photos/videos for approval.
 * Reuses the shared <cadence-album-view> via the /portal/events/:id/gallery API.
 */
@customElement('cadence-portal-event-gallery')
export class CadencePortalEventGallery extends LitElement {
  @property() eventId = '';

  @state() private loading = true;
  @state() private albums: Album[] = [];
  @state() private openAlbum: Album | null = null;

  static styles = css`
    :host { display: block; }
    .grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)); gap: 14px; }
    .album-card { border: 1px solid var(--color-border); border-radius: var(--radius-md); overflow: hidden; cursor: pointer; background: var(--color-bg-primary); transition: border-color 0.15s; }
    .album-card:hover { border-color: var(--color-primary); }
    .album-cover { aspect-ratio: 4/3; background: var(--color-bg-secondary); display: grid; place-items: center; color: var(--color-text-secondary); overflow: hidden; position: relative; }
    .album-cover img, .album-cover video { width: 100%; height: 100%; object-fit: cover; }
    .album-cover svg { width: 38px; height: 38px; opacity: 0.5; }
    .album-cover .cover-play { position: absolute; inset: 0; display: grid; place-items: center; pointer-events: none; }
    .album-cover .cover-play span { background: rgba(0,0,0,0.5); color: #fff; border-radius: 999px; width: 34px; height: 34px; display: grid; place-items: center; }
    .album-cover .cover-play svg { width: 18px; height: 18px; opacity: 1; }
    .album-meta { padding: 10px 12px; }
    .album-meta .name { font-weight: 600; word-break: break-word; }
    .album-meta .sub { color: var(--color-text-secondary); font-size: 0.78rem; margin-top: 2px; }
  `;

  private get base() { return `/portal/events/${this.eventId}/gallery`; }
  private loaded = false;

  updated() { if (this.eventId && !this.loaded) { this.loaded = true; this.load(); } }

  private async load() {
    this.loading = true;
    // The album list lives at the gallery root (the same shape as the main gallery);
    // /gallery/albums/:id is only for a single album.
    try { this.albums = await api.get<Album[]>(this.base); }
    catch (e) { showToast((e as Error).message, 'error'); }
    finally { this.loading = false; }
  }

  private renderCover(a: Album) {
    if (!a.cover_url) return icon('image', 38);
    const inner = isImageKey(a.cover_url)
      ? html`<img src=${mediaUrl(a.cover_url)} alt=${a.name} loading="lazy" />`
      : html`<video src=${mediaUrl(a.cover_url)} preload="metadata" muted></video>`;
    return html`${inner}${a.cover_type === 'video' ? html`<div class="cover-play"><span>${icon('play', 18)}</span></div>` : ''}`;
  }

  render() {
    if (this.loading) return html`<cadence-loading></cadence-loading>`;
    if (this.openAlbum) {
      const a = this.openAlbum;
      return html`
        <cadence-album-view
          .apiBase=${this.base}
          .albumId=${a.id}
          .canUpload=${true}
          .submitMode=${true}
          .showBack=${true}
          .backLabel=${t('portal.gallery.allAlbums')}
          @back=${() => { this.openAlbum = null; this.load(); }}></cadence-album-view>`;
    }
    if (!this.albums.length) {
      return html`<cadence-empty-state heading=${t('portal.gallery.noPhotos')} message=${t('portal.gallery.noPhotosEvent')}></cadence-empty-state>`;
    }
    return html`
      <div class="grid">
        ${this.albums.map((a) => html`
          <div class="album-card" @click=${() => (this.openAlbum = a)}>
            <div class="album-cover">${this.renderCover(a)}</div>
            <div class="album-meta">
              <div class="name">${a.name}</div>
              <div class="sub">${(a.item_count ?? 0) === 1 ? t('portal.gallery.itemOne', { count: a.item_count ?? 0 }) : t('portal.gallery.itemOther', { count: a.item_count ?? 0 })}</div>
            </div>
          </div>`)}
      </div>`;
  }
}
